import React, { useCallback, useEffect, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { fetchShootsByProject } from '../../utils/projectShoots';
import { fetchMirroredEvents, fetchShootEventIds } from '../../utils/calendarMirror';
import { fetchCalendarEntries } from '../../utils/calendarEntries';
import { PANEL_EDGE, PANEL_GROUND } from '../../utils/studioSurfaces';
import { composeWeek, weekLabel, weekWindow, type WeekDay, type WeekItem, type WeekSource } from '../../utils/weekAhead';

/**
 * The week ahead — what is on, day by day, for the next seven days.
 *
 * Built 23 Sep 2026, after Tom and Zeena asked for something that tells them
 * what is coming up before the week starts. The first draft carried three
 * things — the week, a list of gaps to sort, and a to-do list — and Tom was
 * right that three was confusing. So this screen does one thing.
 *
 * What it deliberately does NOT do:
 *
 *   • It does not judge. Nothing here says a shoot is under-prepared or a
 *     client is late. Needs Attention is the screen that says something is
 *     wrong, and splitting that job across two screens would weaken both.
 *   • It does not edit. Adding and moving happens on the Calendar tab, which
 *     already holds the editors and the rules about who owns which layer.
 *     A row does now NAVIGATE there — that is not the same thing, and the
 *     distinction is worth keeping: this screen can send you somewhere, it
 *     cannot change anything.
 *   • **It does not explain itself (24 Sep 2026).** A footnote used to sit
 *     under the last day naming the three sources and pointing at the
 *     Calendar tab. Tom had it removed: the list is legible without a caption.
 *
 * **A clear day is half the height of a busy one (24 Sep 2026).** Every row
 * used to be `py-6` whatever was on it, so in a week with two things on it
 * four empty rows ate two thirds of the screen and the Bloomberg shoot was
 * given no more weight than an empty Sunday.
 *
 * **THE LEAD IS DRAWN BIGGER (16 Oct 2026).** The same complaint one level
 * in: within a busy day, every line was the same size, so
 * *DEADLINE — Al Futtaim Electric Mobility Proposal* and a standing radio
 * slot were typographically identical. Portal OS has no colour to spend on
 * urgency, so emphasis comes from scale, the way a front page sizes its lead
 * story. `utils/weekAhead.ts` decides what qualifies.
 *
 * **Pressing a day opens the Calendar on it (24 Sep 2026).** You could see
 * Friday's shoot here and not get to it. The full version — tapping the SHOOT
 * to open its call sheet — is still blocked, because a project can have
 * several call sheets and this line does not know which one you mean (ATM has
 * six). The day is unambiguous, so the day is what is wired up.
 *
 * ---
 *
 * **`panel` — THE SAME WEEK, IN THE FRONT PAGE'S TOP RIGHT CORNER.**
 *
 * One prop, not a second component: the loading below is the expensive and
 * easy-to-get-wrong part — three fetches and a de-duplication rule — and two
 * copies would drift apart the first time the mirror rule changed.
 *
 * **THE PANEL IS DARK, AND THAT WAS THE WHOLE PROBLEM (5 Nov 2026).**
 *
 * It was paper, and Tom: *"the box just needs to blend better, rather than
 * looking like it has been stuck on the page."* He was right, and the reason
 * is worth keeping: a solid #F4F1EA rectangle was the only flat, evenly lit
 * plane on a page made of photographs, type on the open ground, and one ink
 * band. It had no relatives, so the eye filed it as something laid over the
 * design. On a clear day it was also the brightest and heaviest object on
 * screen while saying that nothing was happening.
 *
 * It now uses the same ground and edge as the Settings tile — see
 * utils/studioSurfaces.ts, which holds both values and the rule. Two members
 * of one family instead of one oddity.
 *
 * **Do not put it back on paper.** Paper is for pages a client reads.
 *
 * ---
 *
 * **Three drawings of the CONTENTS were thrown away before this one. Read
 * all three before trying a fourth.**
 *
 * *The first* (4 Nov) was a ruled table, seven rows, the day's text printed
 * in full. With real data it printed "Tom in Istanbul" four times and a
 * three-day delivery window three times. Tom: "it looks awful."
 *
 * *The second* (5 Nov) kept seven rows, removed every rule, and replaced a
 * repeated line with a short vertical tick meaning "still running". Worse:
 * the rows were all different heights so the ticks never lined up into the
 * continuous line the idea depended on, and with the rules gone there was
 * nothing holding the block together. Tom: "this is shit".
 *
 * *The third* was a set of four variations that were all, again, tables of
 * days.
 *
 * **What is wrong in every one of those is the ROW = DAY assumption.** A
 * four-day trip is one fact, and a layout that insists on a row per day has
 * to say it four times or invent a way of not saying it. So a run is one
 * ROW, and the date cell carries the span: "FRI 6 – SUN 8". Consecutive days
 * merge when their contents are identical, which also collapses a run of
 * empty days into a single "—" rather than five of them. Seven days still
 * accounted for, typically three or four rows, nothing repeated, nothing
 * invented.
 *
 * (The rule Tom gave on 24 Sep — every day gets its own line — was about the
 * full-page drawing below, and about CLEAR days specifically. It still holds
 * there. This panel is 420px wide in the corner of a page that must not
 * scroll, and it answers "what is my week", not "which day shall I put this
 * on".)
 *
 * The time is pulled out of the composed sentence into its own right-hand
 * column, so the eye can run down the clock. `composeWeek` joins its parts
 * with ", ", which is what makes that split safe.
 */

/**
 * **THE LAST WEEK COMPOSED, HELD ACROSS UNMOUNTS. 5 Nov 2026.**
 *
 * Tom: *"when I click to a new tab and go back — the homepage jumps for a
 * split second."*
 *
 * This component is unmounted the moment you leave the front page, so
 * coming back meant `days = []`, `loading = true`, a "Loading…" line about
 * forty pixels tall, three network round trips, and only then the real
 * rows. The panel grew at the end of that — and because the front page is
 * now a fixed-height column whose tile grid takes whatever height is LEFT
 * OVER, the panel growing means all nine tiles shrink. Every photograph on
 * the page resized, together, a beat after it painted.
 *
 * It is worth being plain that this was a cost of the no-scroll layout
 * shipped a day earlier, not an old fault: before that, the tiles were
 * fixed squares and a taller panel merely pushed them down the page where
 * nobody noticed.
 *
 * So the composed week is kept in module scope, keyed by the first day of
 * the window. Coming back inside the same day paints the real rows on the
 * first frame at the height they will keep, and the refetch behind it
 * changes nothing unless the diary actually changed. A new day misses the
 * key and loads as before, which is correct — showing yesterday's week
 * under today's date would be worse than a settle.
 *
 * **It is deliberately not in React state or context.** Module scope dies
 * with the tab, which is the right lifetime for a cache whose only job is
 * to survive a tab change; putting it in context would mean every consumer
 * of PortalContext re-renders when the diary refreshes.
 */
let lastWeek: {
  key: string;
  days: WeekDay[];
} | null = null;

/**
 * The composed line without its time, which the panel sets separately.
 *
 * `composeWeek` builds every line with `sentence()`, which joins its parts
 * with ", " — so the time is always its own comma-separated part and can be
 * removed by exact match rather than by a regular expression hunting for
 * something that looks like a clock. A title that happens to contain "09:00"
 * is therefore left alone.
 */
function withoutTime(item: WeekItem): string {
  if (!item.time) return item.text;
  return item.text.split(', ').filter(part => part !== item.time).join(', ');
}
interface PanelRow {
  key: string;
  /** The day the row opens — the first of the span. */
  iso: string;
  /** "FRI 6", or "FRI 6 – SUN 8" once days have merged into it. */
  label: string;
  /** Kept so the range can be rebuilt as more days join. */
  first: string;
  /** What the day held, joined — two days merge when these match. */
  signature: string;
  isToday: boolean;
  lines: {
    key: string;
    text: string;
    time: string;
    quiet: boolean;
  }[];
}

/** Consecutive days holding exactly the same thing become one row. */
function mergeRuns(days: WeekDay[]): PanelRow[] {
  const rows: PanelRow[] = [];
  for (const day of days) {
    /* NUL-joined so a title containing the separator cannot fake a match. */
    const signature = day.items.map(item => item.text).join('\u0000');
    const open = rows[rows.length - 1];
    if (open && open.signature === signature) {
      open.label = `${open.first} – ${day.label}`;
      open.isToday = open.isToday || day.isToday;
      continue;
    }
    rows.push({
      key: day.iso,
      iso: day.iso,
      label: day.label,
      first: day.label,
      signature,
      isToday: day.isToday,
      lines: day.items.map(item => ({
        key: item.key,
        text: withoutTime(item),
        time: item.time,
        quiet: item.quiet
      }))
    });
  }
  return rows;
}
export function Week({
  onOpenDay,
  panel = false
}: {
  onOpenDay: (iso: string) => void;
  panel?: boolean;
}) {
  const {
    activeProjects
  } = usePortal();

  /* Computed before the state below, because the cache is keyed on it. */
  const window7 = weekWindow();
  const from = window7[0];
  const to = window7[window7.length - 1];

  /* Lazy initialisers, so the cache is read once on mount rather than on
     every render — and so the first paint is the real rows. */
  const [days, setDays] = useState<WeekDay[]>(() => lastWeek && lastWeek.key === from ? lastWeek.days : []);
  const [loading, setLoading] = useState(() => !(lastWeek && lastWeek.key === from));
  const load = useCallback(async () => {
    const [grouped, mirror, shootEventIds, entries] = await Promise.all([fetchShootsByProject(), fetchMirroredEvents(from, to), fetchShootEventIds(), fetchCalendarEntries(from, to)]);
    const shoots: WeekSource['shoots'] = [];
    for (const [projectId, list] of grouped) {
      const project = activeProjects.find(item => item.id === projectId);
      /* Archived projects keep their shoot rows, and a shoot on a finished
         job is not part of anybody's week. */
      if (!project) continue;
      for (const shoot of list) {
        shoots.push({
          shoot,
          projectId,
          client: project.client,
          project: project.name
        });
      }
    }

    /* Everything Portal OS owns in Google, so the mirror does not draw it a
       second time — the same filter the Calendar tab applies, for the same
       reason. */
    const ours = new Set(shootEventIds);
    for (const entry of entries) if (entry.googleEventId) ours.add(entry.googleEventId);
    const composed = composeWeek(window7, {
      shoots,
      entries,
      events: mirror.events.filter(event => !ours.has(event.eventId))
    });
    lastWeek = {
      key: from,
      days: composed
    };
    setDays(composed);
    setLoading(false);
    /* window7 is rebuilt each render but is entirely determined by from/to. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjects, from, to]);
  useEffect(() => {
    void load();
  }, [load]);
  const busy = days.some(day => day.items.length > 0);
  if (panel) {
    const rows = mergeRuns(days);
    return <div style={{
      backgroundColor: PANEL_GROUND,
      borderColor: PANEL_EDGE
    }} className="border px-[18px] pb-[14px] pt-[15px]" data-id="element-2561" data-mp="xk89onrupoeo">
        <div className="flex items-baseline justify-between gap-4" data-id="element-2562" data-mp="2nvbhhzjvw1mn">
          <span className="label text-paper" data-id="element-2563" data-mp="2rxq1wo37b9g1">Week ahead</span>
          <span className="label-sm text-paper/45" data-id="element-2564" data-mp="4qbvndq5sqxm">{weekLabel(window7)}</span>
        </div>

        {loading && <p className="label-sm pt-4 text-paper/40" data-id="element-2565" data-mp="3vwd91sxljwmf">Loading…</p>}

        {!loading && <div className="mt-3" data-id="element-2566" data-mp="3rv00vc717hcc">
            {rows.map(row => <button key={row.key} type="button" onClick={() => onOpenDay(row.iso)} aria-label={`Open ${row.label} on the calendar`} className="grid w-full grid-cols-[6.25rem_1fr] items-start gap-x-3 border-t border-paper/10 py-2 text-left transition-colors duration-150 ease-out hover:bg-paper/[0.05] focus-visible:bg-paper/[0.05] focus-visible:outline-none" data-id="element-2567" data-mp="3rxdvjjxsurjd">
                <span className={`whitespace-nowrap pt-[2px] text-[10px] uppercase tracking-wide2 ${row.isToday ? 'text-paper' : 'text-paper/45'}`} data-id="element-2568" data-mp="9olpnl05dlhn">
                  {row.label}
                </span>

                <span className="min-w-0" data-id="element-2569" data-mp="19q0a8u82emhe">
                  {row.lines.length === 0 ? <span className="block text-[13px] leading-[1.5] text-paper/35" data-id="element-2570" data-mp="1eobjp3ug6uz3">&mdash;</span> : row.lines.map(line => <span key={line.key} className="flex items-baseline justify-between gap-3" data-id="element-2571" data-mp="1jpr7h3o0ffl6">
                        <span className={`min-w-0 text-[13px] leading-[1.5] ${line.quiet ? 'text-paper/45' : 'text-paper/90'}`} data-id="element-2572" data-mp="1nn5dynkb399h">
                          {line.text}
                        </span>
                        {line.time && <span className="shrink-0 text-[11px] tabular-nums text-paper/45" data-id="element-2573" data-mp="27dignye97ci8">
                            {line.time}
                          </span>}
                      </span>)}
                </span>
              </button>)}

            <div className="border-t border-paper/10" data-id="element-2574" data-mp="22pvq8myj8gu5" />

            {!busy && <p className="label-sm pt-3 text-paper/40" data-id="element-2575" data-mp="1yqeh9jjwd1k8">Nothing in the diary this week.</p>}
          </div>}
      </div>;
  }
  return <div data-id="element-2576" data-mp="2ciudo1iam1c6">
      <p className="label text-paper/40" data-id="element-2577" data-mp="27hx0y4g1xzi2">{weekLabel(window7)}</p>
      <h1 className="mt-5 font-display text-4xl leading-[0.95] tracking-tight sm:text-6xl" data-id="element-2578" data-mp="27iot15afxf3l">
        The week ahead
      </h1>

      {loading && <p className="label mt-12 text-paper/35" data-id="element-2579" data-mp="1fr0m4yaflks5">Loading…</p>}

      {!loading && <div className="mt-12" data-id="element-2580" data-mp="1ft3h7wqfy43q">
          {days.map((day, index) => {
        const clear = day.items.length === 0;
        /* A button, so the whole row is the target — on a phone a date
           alone is a 60px-wide thing to hit with a thumb. Everything
           inside is therefore a span: a button may only contain phrasing
           content, so the <ul>/<li>/<p> this used to draw would be
           invalid here and browsers recover from it unpredictably. */
        return <button key={day.iso} type="button" onClick={() => onOpenDay(day.iso)} aria-label={`Open ${day.label} on the calendar`} style={{
          ['--row' as string]: index
        }} className={`row-in flex w-full flex-col border-t border-ruleDark text-left transition-colors duration-150 ease-out hover:bg-paper/[0.04] focus-visible:bg-paper/[0.06] focus-visible:outline-none sm:flex-row sm:gap-10 ${clear ? 'gap-1 py-3' : 'gap-3 py-6'}`} data-id="element-2581" data-mp="qyfkut6ile1v" data-mpi={index}>
                <span className="block shrink-0 sm:w-32" data-id="element-2582" data-mp="10w3sukt4si1z">
                  <span className={`block text-base ${clear ? 'text-paper/45' : 'text-paper'}`} data-id="element-2583" data-mp="10uixwu2wmx3l">
                    {day.label}
                  </span>
                  {day.isToday && <span className="label-sm mt-2 block text-paper/35" data-id="element-2584" data-mp="1tcmvmd9hevj9">Today</span>}
                </span>

                <span className="block min-w-0 flex-1" data-id="element-2585" data-mp="238myymh3znk6">
                  {clear ? <span className="block text-[1.0625rem] leading-snug text-paper/45" data-id="element-2586" data-mp="1y8r011n9qmbh">Clear</span> : <span className="flex flex-col gap-3.5" data-id="element-2587" data-mp="1t8w5mo4abelo">
                      {day.items.map(item => <span key={item.key} className={`block leading-snug ${item.quiet ? 'text-[1.0625rem] text-paper/45' : item.lead ? 'text-[1.5rem] text-paper' : 'text-[1.0625rem] text-paper/90'}`} data-id="element-2588" data-mp="1oaku88yccw1c">
                          {item.text}
                        </span>)}
                    </span>}
                </span>
              </button>;
      })}

          <div className="border-t border-ruleDark" data-id="element-2589" data-mp="3881pikxj5m0w" />

          {/* The only line left under the days, and only when there are none
              worth reading: an entirely empty screen looks broken. */}
          {!busy && <p className="label-sm mt-8 text-paper/35" data-id="element-2590" data-mp="3h8hrfpymkl3n">
              Nothing in the diary for the next seven days.
            </p>}
        </div>}
    </div>;
}